import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../../stores/authStore';
import { useLayoutsStore } from '../../stores/layoutsStore';
import { useDialogStore } from '../../stores/dialogStore';
import ProtectedImage from '../../components/ProtectedImage';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable Layout Item Component
const SortableLayoutItem = ({ layout, onToggleActive, onUploadPreview, type }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: layout.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      onUploadPreview(layout.id, e.target.files[0]);
      e.target.value = '';
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 mb-3"
    >
      <div className="flex items-start gap-4">
        {/* Drag handle */}
        <div
          {...attributes}
          {...listeners}
          className="cursor-move text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 pt-1"
          title="Drag to reorder"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
          </svg>
        </div>

        {/* Preview image */}
        <div className="flex-shrink-0">
          {layout.preview_uuid ? (
            <div className="relative w-24 h-24 rounded overflow-hidden bg-gray-100 dark:bg-gray-700">
              <ProtectedImage
                uuid={layout.preview_uuid}
                alt={layout.label}
                className="w-full h-full object-cover"
              />
              <label className="absolute inset-0 bg-black bg-opacity-50 opacity-0 hover:opacity-100 transition-opacity cursor-pointer flex items-center justify-center">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </label>
            </div>
          ) : (
            <label className="w-24 h-24 rounded border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-primary-600 dark:hover:border-primary-400 flex items-center justify-center cursor-pointer bg-gray-50 dark:bg-gray-700 transition-colors">
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </label>
          )}
        </div>

        {/* Layout details */}
        <div className="flex-1">
          <div className="flex items-start justify-between">
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-white">{layout.label}</h4>
              <p className="text-sm text-gray-500 dark:text-gray-400 font-mono">{layout.code}</p>
              {type === 'media' && layout.type_label && (
                <span className="inline-block mt-1 px-2 py-1 text-xs font-medium rounded bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300">
                  {layout.type_label}
                </span>
              )}
              {layout.description && (
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-2">{layout.description}</p>
              )}
            </div>

            {/* Active toggle */}
            <button
              onClick={() => onToggleActive(layout.id, !layout.is_active)}
              className={`flex-shrink-0 relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                layout.is_active
                  ? 'bg-primary-600'
                  : 'bg-gray-200 dark:bg-gray-700'
              }`}
              title={layout.is_active ? 'Active' : 'Inactive'}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  layout.is_active ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Layouts = () => {
  const { accessToken, user } = useAuthStore();
  const { alert } = useDialogStore();

  // Check if user is admin
  if (!user || user.is_admin !== 1) {
    return (
      <div className="surface rounded-lg p-8 text-center">
        <h2 className="text-2xl font-bold text-primary mb-4">Access Denied</h2>
        <p className="text-secondary">You must be an administrator to access this page.</p>
      </div>
    );
  }
  const {
    mediaLayouts,
    widgetLayouts,
    isLoading,
    error,
    fetchMediaLayouts,
    fetchWidgetLayouts,
    updateMediaLayout,
    updateWidgetLayout,
    reorderMediaLayouts,
    reorderWidgetLayouts,
    uploadMediaLayoutPreview,
    uploadWidgetLayoutPreview,
    clearError,
  } = useLayoutsStore();

  const [localMediaLayouts, setLocalMediaLayouts] = useState([]);
  const [localWidgetLayouts, setLocalWidgetLayouts] = useState([]);

  // Drag sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (accessToken) {
      fetchMediaLayouts(accessToken);
      fetchWidgetLayouts(accessToken);
    }
  }, [accessToken]);

  // Sync from store to local state
  useEffect(() => {
    setLocalMediaLayouts(mediaLayouts);
  }, [mediaLayouts]);

  useEffect(() => {
    setLocalWidgetLayouts(widgetLayouts);
  }, [widgetLayouts]);

  // Group media layouts by type
  const singleLayouts = localMediaLayouts.filter(l => l.type_code === 'SINGLE');
  const galleryLayouts = localMediaLayouts.filter(l => l.type_code === 'GALLERY');

  // Toggle active state
  const handleToggleMediaActive = async (layoutId, isActive) => {
    const result = await updateMediaLayout(layoutId, { is_active: isActive }, accessToken);
    if (result.success) {
      await fetchMediaLayouts(accessToken);
    } else {
      await alert(result.error || 'Failed to update layout', 'Error');
    }
  };

  const handleToggleWidgetActive = async (layoutId, isActive) => {
    const result = await updateWidgetLayout(layoutId, { is_active: isActive }, accessToken);
    if (result.success) {
      await fetchWidgetLayouts(accessToken);
    } else {
      await alert(result.error || 'Failed to update layout', 'Error');
    }
  };

  // Upload preview
  const handleUploadMediaPreview = async (layoutId, file) => {
    const result = await uploadMediaLayoutPreview(layoutId, file, accessToken);
    if (result.success) {
      await fetchMediaLayouts(accessToken);
    } else {
      await alert(result.error || 'Failed to upload preview', 'Error');
    }
  };

  const handleUploadWidgetPreview = async (layoutId, file) => {
    const result = await uploadWidgetLayoutPreview(layoutId, file, accessToken);
    if (result.success) {
      await fetchWidgetLayouts(accessToken);
    } else {
      await alert(result.error || 'Failed to upload preview', 'Error');
    }
  };

  // Drag end handlers
  const handleMediaDragEnd = async (event, type) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const layouts = type === 'SINGLE' ? singleLayouts : galleryLayouts;
    const oldIndex = layouts.findIndex(l => l.id === active.id);
    const newIndex = layouts.findIndex(l => l.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    // Reorder within the type group
    const reordered = arrayMove(layouts, oldIndex, newIndex);

    // Update local state optimistically
    const otherLayouts = type === 'SINGLE' ? galleryLayouts : singleLayouts;
    setLocalMediaLayouts([...reordered, ...otherLayouts].sort((a, b) => a.sort_order - b.sort_order));

    // Send to backend
    const layoutOrder = reordered.map(l => l.id);
    const result = await reorderMediaLayouts(layoutOrder, accessToken);

    if (!result.success) {
      // Revert on error
      setLocalMediaLayouts(mediaLayouts);
      await alert(result.error || 'Failed to reorder layouts', 'Error');
    } else {
      // Refresh to get correct sort_order from server
      await fetchMediaLayouts(accessToken);
    }
  };

  const handleWidgetDragEnd = async (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = localWidgetLayouts.findIndex(l => l.id === active.id);
    const newIndex = localWidgetLayouts.findIndex(l => l.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    // Update local state optimistically
    const reordered = arrayMove(localWidgetLayouts, oldIndex, newIndex);
    setLocalWidgetLayouts(reordered);

    // Send to backend
    const layoutOrder = reordered.map(l => l.id);
    const result = await reorderWidgetLayouts(layoutOrder, accessToken);

    if (!result.success) {
      // Revert on error
      setLocalWidgetLayouts(widgetLayouts);
      await alert(result.error || 'Failed to reorder layouts', 'Error');
    } else {
      // Refresh to get correct sort_order from server
      await fetchWidgetLayouts(accessToken);
    }
  };

  if (isLoading && mediaLayouts.length === 0 && widgetLayouts.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Media Layouts Column */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Media Layouts</h2>

          {/* SINGLE Layouts */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-3">Single Image/Video</h3>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={(event) => handleMediaDragEnd(event, 'SINGLE')}
            >
              <SortableContext
                items={singleLayouts.map(l => l.id)}
                strategy={verticalListSortingStrategy}
              >
                {singleLayouts.map(layout => (
                  <SortableLayoutItem
                    key={layout.id}
                    layout={layout}
                    type="media"
                    onToggleActive={handleToggleMediaActive}
                    onUploadPreview={handleUploadMediaPreview}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </div>

          {/* GALLERY Layouts */}
          <div>
            <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-3">Gallery/Carousel</h3>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={(event) => handleMediaDragEnd(event, 'GALLERY')}
            >
              <SortableContext
                items={galleryLayouts.map(l => l.id)}
                strategy={verticalListSortingStrategy}
              >
                {galleryLayouts.map(layout => (
                  <SortableLayoutItem
                    key={layout.id}
                    layout={layout}
                    type="media"
                    onToggleActive={handleToggleMediaActive}
                    onUploadPreview={handleUploadMediaPreview}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </div>
        </div>

        {/* Widget Layouts Column */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Widget Layouts</h2>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleWidgetDragEnd}
          >
            <SortableContext
              items={localWidgetLayouts.map(l => l.id)}
              strategy={verticalListSortingStrategy}
            >
              {localWidgetLayouts.map(layout => (
                <SortableLayoutItem
                  key={layout.id}
                  layout={layout}
                  type="widget"
                  onToggleActive={handleToggleWidgetActive}
                  onUploadPreview={handleUploadWidgetPreview}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      </div>
    </div>
  );
};

export default Layouts;
